import { useGraphQLClient, useSubscribe, useSubscription } from "@package/graphql/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DocumentVars } from "@package/graphql";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useUserId } from "../../context/auth/hooks";
import { useLogout } from "../../context/auth/provider";
import { useFeatureFlags } from "../../context/flags";
import { parseRoles } from "../../utils/roles";
import { readFragment } from "../../graphql";
import * as queries from "./queries";
import { STAFF_ROLE, isInternalTier, isStaff, resolveTier, subscriptionTier } from "./utils";

export * from "./utils";

/**
 * Switched on per org once its entitlement grants are in place. Off, the app
 * gates on the legacy `subscription_tier` column alone.
 */
export const ENTITLEMENTS_FLAG = "entitlements";

const useEntitlementsEnabled = () => Boolean(useFeatureFlags(ENTITLEMENTS_FLAG)[0]);

// We need to pick up on changes to the current user in real-time to disable access to features their tier does not allow etc. so we use a subscription.
export const useCurrentUserSub = (userId?: string) => {
  const graphql = useGraphQLClient();
  const client = useQueryClient();
  const logout = useLogout();
  const logoutRequestedRef = useRef<string | null>(null);
  const initialLoadRef = useRef(true);

  const qry = useQuery({
    queryKey: ["currentUser", userId],
    queryFn: () =>
      graphql
        .execute(queries.CurrentUserQuery, { id: userId ?? "" })
        .then((res) => readFragment(queries.CurrentUserFragment, res.data?.folio_user_by_pk)),
  });

  useSubscribe({
    document: queries.CurrentUserSub,
    variables: { id: userId ?? "" },
    next: (res) => {
      const data = readFragment(queries.CurrentUserFragment, res.data?.folio_user_by_pk);
      client.setQueryData(["currentUser", userId], data);

      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        logoutRequestedRef.current = data?.logout_requested_at ?? null;
        return;
      }

      if (data?.logout_requested_at && data.logout_requested_at !== logoutRequestedRef.current) {
        logoutRequestedRef.current = data.logout_requested_at;
        logout();
      }
    },
  });

  return qry;
};

// this effect, redirecting to folio.artis.works, has nothing to do with getting the current user
// but I don't know where else this could safely go at the moment.
// introduced in https://github.com/artisworks/artis-webapp/commit/08f7c4955832d6920f45692a11246294e9cde1cd
// and tweaked here https://github.com/artisworks/artis-webapp/commit/5016eb80fffda0fb19cd4cafafd6e24597f92697
const useRedirectToFolio = (
  userId: string | undefined,
  usingWebapp2: boolean | undefined,
  staff: boolean,
  /** @deprecated transitional, until internal users hold the staff role */
  subscriptionTier: string | undefined,
) => {
  useEffect(() => {
    // Artis staff keep using the newer web app without the webapp2 flag. The
    // tier check is transitional — see docs/subscription-tier-removal.md.
    const exempt = staff || isInternalTier(subscriptionTier);
    if (
      userId &&
      !usingWebapp2 &&
      !exempt &&
      !window.location.pathname.includes("trading-chart") &&
      (window.location.hostname === "app.artis.works" || window.location.hostname.includes("artis-webapp"))
    ) {
      window.location.href = "https://folio.artis.works";
    }
  }, [userId, usingWebapp2, staff, subscriptionTier]);
};

// TODO: this is used by a bunch of components so there's a lot of repeated work here
// this is probably better off as a context/provider
export function useCurrentUser() {
  const userId = useUserId();
  const auth0User = useAuth0();
  const qry = useCurrentUserSub(userId);
  const entitlementsEnabled = useEntitlementsEnabled();

  const data = useMemo(() => {
    const row = qry.data;
    const roles = row?.roles ? parseRoles(row.roles) : undefined;
    const username = row?.username;

    // Gate on the entitlement level, falling back to the legacy column when no
    // grant covers now. The raw column is kept as `legacy_subscription_tier`
    // for the transitional `isInternalTier` checks (redirect, multisession,
    // Share Page) — a live grant can resolve an internal user to another tier,
    // which would drop those bypasses. See docs/subscription-tier-removal.md.
    const resolved = entitlementsEnabled ? resolveTier(row?.entitlement?.level, row?.subscription_tier) : undefined;
    const user = row && {
      ...row,
      subscription_tier: resolved ?? row.subscription_tier,
      legacy_subscription_tier: row.subscription_tier,
    };

    return {
      user,
      roles,
      username,
      data: user,
      /** Level 0 with no usable legacy tier: no live grant and nothing to fall back to. */
      entitlement: row?.entitlement,
      firstname: row?.firstname ?? auth0User.user?.given_name,
      lastname: row?.lastname ?? auth0User.user?.family_name,
      isLoading: qry.isLoading,
    };
  }, [qry.isLoading, qry.data, auth0User, entitlementsEnabled]);

  useRedirectToFolio(
    data.user?.id,
    data.user?.webapp2 ?? undefined,
    isStaff(data.user?.roles),
    data.user?.legacy_subscription_tier ?? undefined,
  );

  return data;
}

export function useUserHasRole(role: string) {
  const { roles: userRoles } = useCurrentUser();
  return Boolean(userRoles?.has(role));
}

/** Whether the signed-in user is Artis staff. */
export const useIsStaff = () => useUserHasRole(STAFF_ROLE);

export function useUserSubscriptionTier() {
  const { user } = useCurrentUser();
  const entitlementsEnabled = useEntitlementsEnabled();

  const parsed = subscriptionTier.safeParse(user?.subscription_tier);
  const tier = parsed.success
    ? parsed.data
    : user && entitlementsEnabled
      ? subscriptionTier.enum.artis_lite
      : undefined;

  const liteUser = tier === subscriptionTier.enum.artis_lite;
  const enhancedUser = tier === subscriptionTier.enum.artis_enhanced;
  const enhancedNoExchUser = tier === subscriptionTier.enum.artis_enhanced_noexch;
  const professionalUser = tier === subscriptionTier.enum.artis_professional;
  const internalUser = tier === subscriptionTier.enum.artis_internal;

  return useMemo(() => {
    const disabledFeatures =
      professionalUser || internalUser
        ? undefined
        : {
            showFormula: liteUser,
            headerSize: liteUser,
            curveDetails: liteUser,
            spreader: liteUser,
            managePages: liteUser,
            pauseExchangeData: liteUser,
            changeSource: liteUser || enhancedUser || enhancedNoExchUser,
            setAllFuturesFields: liteUser || enhancedUser || enhancedNoExchUser,
            statusRow: liteUser || enhancedUser || enhancedNoExchUser,
            charts: liteUser,
            hfc: liteUser,
            createPage: liteUser,
            createCurve: liteUser || enhancedUser || enhancedNoExchUser,
            editCurve: liteUser || enhancedUser || enhancedNoExchUser,
            insertCurve: liteUser,
            removeCurve: liteUser,
            priceAlerts: liteUser || enhancedUser,
            plotLiveChart: liteUser,
            conditionalFormatting: liteUser,
            reconfirmData: liteUser || enhancedUser || enhancedNoExchUser,
            catalogue: liteUser,
            tracePrecedents: liteUser || enhancedUser || enhancedNoExchUser,
            scratchpad: liteUser || enhancedUser || enhancedNoExchUser,
          };

    return {
      liteUser,
      enhancedUser,
      enhancedNoExchUser,
      professionalUser,
      internalUser,
      disabledFeatures,
    };
  }, [liteUser, enhancedUser, enhancedNoExchUser, professionalUser, internalUser]);
}

export const usePermissionRowsSub = (userId: string | undefined, enabled = true) =>
  useSubscription(queries.MarketUserPermissionsSub, {
    variables: { folio_user: userId ?? "" },
    enabled: enabled && !!userId,
  });

/** Folder name, type and curves for the given packages, read once on demand. */
export const usePermissionFolderDetails = () => {
  const graphql = useGraphQLClient();
  return useCallback(
    (userId: string, packages: number[]) =>
      graphql
        .execute(queries.PermissionFolderDetailsQuery, { folio_user: userId, packages })
        .then((res) => res.data?.permission ?? []),
    [graphql],
  );
};

export function useHasExchangePermissions() {
  const userId = useUserId();
  const { data } = useSubscription(queries.UserExchangePermissionsSub, {
    enabled: !!userId,
    variables: { folio_user: userId ?? "" },
  });
  return (data?.permission?.length ?? 0) > 0;
}

export const useUserExchangeUsage = (userId: string) =>
  useSubscription(queries.LastExchangeUsageForSingleUser, { variables: { userId } });

export const useUserAmendExchangeUsage = () => {
  const graphql = useGraphQLClient();
  return useMutation({
    mutationFn: (arg: DocumentVars<typeof queries.amendExchangeUsage>) =>
      graphql.execute(queries.amendExchangeUsage, arg),
  });
};

export const useOrgUserProfiles = (orgId: number | undefined) => {
  const graphql = useGraphQLClient();
  return useQuery({
    enabled: !!orgId,
    queryKey: ["orgUsers", orgId],
    queryFn: async () => {
      if (orgId === undefined) return [];
      const result = await graphql.execute(queries.OrgUserProfilesQuery, { orgId: orgId });
      return readFragment(queries.OrgUserFragment, result.data?.folio_user_basic_profile ?? []);
    },
  });
};

export const useUserProfilesByIds = (userIds: string[] | undefined) => {
  const graphql = useGraphQLClient();
  return useQuery({
    enabled: !!userIds && userIds.length > 0,
    queryKey: ["userProfilesByIds", userIds?.join(",")],
    queryFn: async () => {
      if (!userIds || userIds.length === 0) return [];
      const result = await graphql.execute(queries.UserProfilesByIdsQuery, { userIds });
      return readFragment(queries.OrgUserFragment, result.data?.folio_user_basic_profile ?? []);
    },
  });
};

/** Persona options for the profile form, from the `persona_enum` type, with a
 *  hardcoded fallback so the form still works if introspection is unavailable. */
export function usePersonaOptions() {
  const graphqlClient = useGraphQLClient();

  return useQuery({
    queryKey: ["personaOptions"],
    queryFn: async () => {
      const fallback = ["Trader", "Broker", "Analyst", "Ops", "Quant", "Risk"];

      let enumValues: string[] = [];
      try {
        const result = await graphqlClient.execute(queries.PersonaEnumQuery);
        enumValues = result.data?.__type?.enumValues?.map((v) => v.name) ?? [];
      } catch {
        return fallback;
      }

      if (enumValues.length === 0) return fallback;

      const rest = enumValues.filter((v) => v !== "Trader" && v !== "Broker").sort();
      return ["Trader", "Broker"].filter((v) => enumValues.includes(v)).concat(rest);
    },
  });
}
